/**
 * Platform Utilities
 *
 * Centralized platform detection and utilities.
 * Reduces direct dependency on React Native Platform across codebase.
 */

import { Platform, Dimensions } from 'react-native';

// Platform constants
export const PLATFORMS = {
  WEB: 'web',
  IOS: 'ios',
  ANDROID: 'android',
};

/**
 * Check if running on web
 * @returns {boolean}
 */
export function isWeb() {
  return Platform.OS === 'web';
}

/**
 * Check if running on iOS
 * @returns {boolean}
 */
export function isIOS() {
  return Platform.OS === 'ios';
}

/**
 * Check if running on Android
 * @returns {boolean}
 */
export function isAndroid() {
  return Platform.OS === 'android';
}

/**
 * Check if running on native (iOS or Android)
 * @returns {boolean}
 */
export function isNative() {
  return Platform.OS === 'ios' || Platform.OS === 'android';
}

/**
 * Check if running on mobile (native platforms)
 * @returns {boolean}
 */
export function isMobile() {
  return isNative();
}

/**
 * Get current platform name
 * @returns {string}
 */
export function getPlatform() {
  return Platform.OS;
}

/**
 * Select value based on platform
 * @param {Object} options - Platform-specific values
 * @param {*} options.ios - Value for iOS
 * @param {*} options.android - Value for Android
 * @param {*} options.web - Value for web
 * @param {*} options.native - Value for iOS/Android (fallback)
 * @param {*} options.default - Default value
 * @returns {*} Selected value
 *
 * @example
 * const fontSize = selectByPlatform({ web: 16, native: 14, default: 14 });
 */
export function selectByPlatform(options) {
  const { ios, android, web, native, default: defaultValue } = options;

  if (Platform.OS === 'web' && web !== undefined) return web;
  if (Platform.OS === 'ios' && ios !== undefined) return ios;
  if (Platform.OS === 'android' && android !== undefined) return android;
  if (isNative() && native !== undefined) return native;

  return defaultValue;
}

/**
 * Get platform-specific styles
 * @param {Object} baseStyles - Base styles for all platforms
 * @param {Object} platformStyles - Platform-specific overrides
 * @param {Object} [platformStyles.ios] - iOS-specific styles
 * @param {Object} [platformStyles.android] - Android-specific styles
 * @param {Object} [platformStyles.web] - Web-specific styles
 * @returns {Object} Merged styles
 *
 * @example
 * const styles = getPlatformStyles(
 *   { padding: 10, margin: 5 },
 *   { web: { padding: 20 }, ios: { margin: 10 } }
 * );
 */
export function getPlatformStyles(baseStyles, platformStyles = {}) {
  const platformSpecific = platformStyles[Platform.OS] || {};
  return { ...baseStyles, ...platformSpecific };
}

/**
 * Check if screen is in portrait orientation
 * @returns {boolean}
 */
export function isPortrait() {
  const { width, height } = Dimensions.get('window');
  return height >= width;
}

/**
 * Check if screen is in landscape orientation
 * @returns {boolean}
 */
export function isLandscape() {
  return !isPortrait();
}

/**
 * Get screen dimensions
 * @returns {{ width: number, height: number }}
 */
export function getScreenDimensions() {
  return Dimensions.get('window');
}

/**
 * Check if screen width is below breakpoint (mobile-sized)
 * @param {number} breakpoint - Width breakpoint (default: 768)
 * @returns {boolean}
 */
export function isMobileWidth(breakpoint = 768) {
  const { width } = Dimensions.get('window');
  return width < breakpoint;
}

/**
 * Check if screen width is above breakpoint (tablet/desktop)
 * @param {number} breakpoint - Width breakpoint (default: 768)
 * @returns {boolean}
 */
export function isDesktopWidth(breakpoint = 768) {
  return !isMobileWidth(breakpoint);
}

// Re-export Platform for cases where direct access is needed
export { Platform };
